import React, { useEffect, useState } from 'react';
import { customAgentAPI } from '../services/api.js';

export default function AgentPicker({ open, onClose, onSelect, extraItems }) {
  const [items, setItems] = useState([]); // combined agents + chains
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [agentsRes, chainsRes] = await Promise.allSettled([
          customAgentAPI.list(),
          customAgentAPI.listChains(),
        ]);
        const agents = (agentsRes.status === 'fulfilled') ? (Array.isArray(agentsRes.value) ? agentsRes.value : (agentsRes.value?.agents || [])) : [];
        const chains = (chainsRes.status === 'fulfilled') ? (Array.isArray(chainsRes.value) ? chainsRes.value : (chainsRes.value?.chains || [])) : [];

        // Normalize and tag
        const normAgents = agents.map(a => ({ ...a, _type: 'agent', id: a._id || a.id }));
        const normChains = chains.map(c => ({ ...c, _type: 'workflow', id: c._id || c.id }));
        const extras = Array.isArray(extraItems) ? extraItems : [];
        const combined = [
          ...extras,
          ...normAgents.filter(a => !extras.some(item => String(item.id || item._id) === String(a.id || a._id))),
          ...normChains.filter(c => !extras.some(item => String(item.id || item._id) === String(c.id || c._id))),
        ];
        if (!cancelled) setItems(combined);
      } catch (e) {
        if (!cancelled) setItems([]);
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [open, extraItems]);

  if (!open) return null;
  const q = (query || '').toLowerCase();
  const filtered = items.filter(i => (i.name || '').toLowerCase().includes(q)).slice(0, 200);

  const ink = 'var(--lyra-color-text)';
  const muted = 'var(--lyra-color-text-muted)';
  const border = 'var(--lyra-color-border)';
  const badge = { fontSize: 10.5, fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: 'var(--app-surface-3)', color: muted, border: `0.5px solid ${border}`, whiteSpace: 'nowrap' };
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(8,12,24,0.5)', padding: 16 }} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div role="dialog" aria-label="Add agent or workflow" style={{ width: 'min(520px, 100%)', maxHeight: '70vh', display: 'flex', flexDirection: 'column', background: 'var(--lyra-color-surface)', color: ink, border: `0.5px solid var(--app-border-strong)`, borderRadius: 12, boxShadow: '0 18px 44px rgba(8,12,24,.28)', overflow: 'hidden' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: `0.5px solid ${border}` }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: ink }}>Add Agent / Workflow</div>
          <button onClick={onClose} aria-label="Close" style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: muted, fontSize: 15, lineHeight: 1 }}>✕</button>
        </div>
        <div style={{ padding: '10px 16px 6px' }}>
          <input autoFocus placeholder="Search agents or workflows" value={query} onChange={e => setQuery(e.target.value)}
            style={{ width: '100%', boxSizing: 'border-box', padding: '7px 10px', borderRadius: 8, border: `1px solid ${border}`, background: 'var(--lyra-color-surface-2)', color: ink, fontSize: 12.5, fontFamily: 'inherit', outline: 'none' }} />
        </div>
        <div style={{ overflowY: 'auto', padding: '4px 16px 14px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          {loading ? <div style={{ fontSize: 12, color: muted, padding: 8 }}>Loading…</div> : filtered.length === 0 ? (
            <div style={{ fontSize: 12, color: muted, padding: 8 }}>Nothing matches.</div>
          ) : (
            filtered.map(i => (
              <div key={i.id || i._id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 8, border: `0.5px solid ${border}`, background: 'var(--lyra-color-surface)' }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', minWidth: 0 }}>
                  <div style={{ width: 28, height: 28, borderRadius: 8, flexShrink: 0, background: 'var(--app-primary-soft)', color: 'var(--app-primary-deep)', fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{(i.name || '?').charAt(0).toUpperCase()}</div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{i.name}</div>
                    <div style={{ fontSize: 11.5, color: muted, lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{i.description || i.shortDescription || (i._type === 'workflow' ? 'Workflow/chain' : '')}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                  <span style={badge}>{i._type === 'workflow' ? 'Workflow' : i._type === 'member' ? 'Member' : 'Agent'}</span>
                  <button onClick={() => { onSelect(i); onClose(); }}
                    style={{ padding: '4px 12px', borderRadius: 7, border: 'none', background: 'var(--lyra-color-primary)', color: 'var(--lyra-color-primary-fg, var(--app-on-accent))', fontSize: 11.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Attach</button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
